import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  Platform,
} from "react-native";
import { useRouter } from "expo-router";

const NAVY = "#1B2A4A";
const NAVY_DARK = "#111C30";
const ORANGE = "#F5A623";
const WHITE = "#FFFFFF";
const LIGHT_GRAY = "#F8F9FA";
const TEXT = "#1F2937";
const TEXT_MUTED = "#6B7280";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <Text style={styles.p}>{children}</Text>;
}

function Li({ children }: { children: React.ReactNode }) {
  return (
    <View style={styles.li}>
      <Text style={styles.liBullet}>•</Text>
      <Text style={styles.liText}>{children}</Text>
    </View>
  );
}

export default function PrivacyScreen() {
  const router = useRouter();

  return (
    <View style={{ flex: 1, backgroundColor: WHITE }}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => router.back()}
          style={styles.backButton}
        >
          <Text style={styles.backText}>← Back</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Privacy Policy</Text>
        <View style={{ width: 60 }} />
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.hero}>
          <Text style={styles.heroTitle}>Privacy Policy</Text>
          <Text style={styles.heroDate}>Last updated: April 18, 2026</Text>
        </View>

        <View style={styles.body}>
          <P>
            Dash-Hound LLC ("we," "us," or "our"), a New Jersey limited liability company, operates the DashHound mobile and
            web application (the "App"). This Privacy Policy explains how we
            collect, use, disclose, and safeguard your information when you use
            our App.
          </P>
          <P>
            Please read this policy carefully. If you disagree with its terms,
            please discontinue use of the App.
          </P>

          <Section title="1. Information We Collect">
            <P>We collect the following types of information:</P>
            <P style={{ fontWeight: "600", color: NAVY, marginBottom: 4 }}>
              Account Information
            </P>
            <Li>Email address</Li>
            <Li>Display name</Li>
            <Li>Password (stored as a secure hash — never in plain text)</Li>
            <Li>State and zip code (optional, used for event recommendations)</Li>

            <P style={{ fontWeight: "600", color: NAVY, marginTop: 12, marginBottom: 4 }}>
              Dog Profile Information
            </P>
            <Li>Dog name, breed, sex, and AKC registration details</Li>
            <Li>AKC registered name (optional)</Li>
            <Li>Profile photos you choose to upload (stored as encoded data)</Li>

            <P style={{ fontWeight: "600", color: NAVY, marginTop: 12, marginBottom: 4 }}>
              Run & Performance Data
            </P>
            <Li>Fast CAT run times you log</Li>
            <Li>Event dates and run notes</Li>
            <Li>Calculated speed, points, and title progress</Li>

            <P style={{ fontWeight: "600", color: NAVY, marginTop: 12, marginBottom: 4 }}>
              Location Information
            </P>
            <Li>State and zip code you provide in your profile (not GPS or device location)</Li>
            <Li>Used to show nearby events and send event notification emails</Li>

            <P style={{ fontWeight: "600", color: NAVY, marginTop: 12, marginBottom: 4 }}>
              Technical Information
            </P>
            <Li>Authentication tokens stored in local storage / secure device storage</Li>
            <Li>Device type and operating system (for compatibility)</Li>
          </Section>

          <Section title="2. How We Use Your Information">
            <P>We use the information we collect to:</P>
            <Li>Provide, operate, and maintain the App</Li>
            <Li>Create and manage your account</Li>
            <Li>Calculate and display your dog's speed, points, title progress, and projected breed rankings</Li>
            <Li>Send transactional emails (password reset, welcome emails)</Li>
            <Li>Send notification emails about ranking changes and new events in your area (you can opt out in Settings)</Li>
            <Li>Display your dog's public profile if you choose to make it public</Li>
            <Li>Generate shareable profile images for social media sharing</Li>
            <Li>Respond to your feedback and support requests</Li>
            <Li>Monitor for fraud or abuse</Li>
          </Section>

          <Section title="3. AKC Events and Rankings Data">
            <P>
              DashHound displays event listings and breed rankings sourced from
              publicly available AKC resources. This data belongs to the American
              Kennel Club and is not user-submitted. We do not store or share your
              personal information in connection with this data.
            </P>
          </Section>

          <Section title="4. Public Dog Profiles">
            <P>
              You may choose to make your dog's profile public. When a profile is
              made public, the following information becomes visible to anyone with
              the profile link — no login required:
            </P>
            <Li>Dog name, breed, registered name, and sex</Li>
            <Li>Dog photo (if uploaded)</Li>
            <Li>Run history, performance stats, and title progress</Li>
            <Li>Projected breed ranking</Li>
            <P>
              Public profiles are off by default. You can make a profile public or
              private at any time from the dog's profile page. When you set a profile
              to private, the public page is immediately removed.
            </P>
          </Section>

          <Section title="5. Screenshot Import & Third-Party AI Processing">
            <P>
              The App offers a feature to import run history by uploading screenshots
              from other apps. When you use this feature:
            </P>
            <Li>Your screenshot image is sent to Anthropic's Claude AI service for text extraction</Li>
            <Li>The image is processed in real-time and is not stored by us after processing</Li>
            <Li>Only the extracted run data (dates and times) is saved to your account</Li>
            <Li>Anthropic's use of the data is governed by their privacy policy</Li>
            <P>
              This feature is entirely optional. You can always log runs manually or
              import via CSV instead.
            </P>
          </Section>

          <Section title="6. Email Notifications">
            <P>
              If you provide your state and/or zip code, we may send you email
              notifications about:
            </P>
            <Li>New Fast CAT events posted near your location (within 100 miles)</Li>
            <Li>Changes to your breed's AKC rankings</Li>
            <P>
              You can enable or disable these notifications at any time in the App's
              Settings page. We use Amazon Simple Email Service (SES) to send emails.
              We will never share your email with third parties for marketing purposes.
            </P>
          </Section>

          <Section title="8. Data We Do NOT Collect">
            <Li>
              We do NOT track your GPS location. Event location data is sourced
              from AKC public listings, not from your device.
            </Li>
            <Li>
              We do NOT collect payment information. DashHound is free to use.
            </Li>
            <Li>
              We do NOT sell, rent, or trade your personal information to third
              parties.
            </Li>
            <Li>
              We do NOT display advertising or share data with advertising
              networks.
            </Li>
          </Section>

          <Section title="9. Data Storage and Security">
            <P>
              Your data is stored on secure servers. We use industry-standard
              encryption for data in transit (HTTPS/TLS). Passwords are hashed
              using strong cryptographic algorithms and are never stored or
              transmitted in plain text.
            </P>
            <P>
              While we implement reasonable security measures, no method of
              transmission over the internet or electronic storage is 100% secure.
              We cannot guarantee absolute security of your information.
            </P>
          </Section>

          <Section title="10. Cookies and Local Storage">
            <P>
              When you use the web version of DashHound, we store authentication
              tokens in your browser's local storage to keep you signed in. On
              mobile, we use your device's secure storage for the same purpose.
              These tokens expire and are cleared when you sign out.
            </P>
            <P>
              We do not use third-party tracking cookies or advertising cookies.
            </P>
          </Section>

          <Section title="11. Sharing Your Information">
            <P>
              We do not sell, trade, or otherwise transfer your personal
              information to outside parties except in the following limited
              circumstances:
            </P>
            <Li>
              Service providers who assist in operating the App (e.g., hosting
              providers), subject to confidentiality agreements
            </Li>
            <Li>
              When required by law, regulation, or legal process
            </Li>
            <Li>
              To protect the rights, property, or safety of DashHound, our users,
              or the public
            </Li>
          </Section>

          <Section title="12. Children's Privacy">
            <P>
              DashHound is not directed to children under 13 years of age. We do
              not knowingly collect personal information from children under 13. If
              you believe we have inadvertently collected such information, please
              contact us immediately and we will delete it.
            </P>
          </Section>

          <Section title="13. Your Rights and Data Deletion">
            <P>You have the right to:</P>
            <Li>Access the personal information we hold about you</Li>
            <Li>Correct inaccurate data through the App's edit features</Li>
            <Li>
              Delete your account and all associated data directly from the App
              (Settings → Delete Account). Deletion is immediate and permanent —
              all your dogs, runs, and profile data will be removed.
            </Li>
            <Li>
              You may also request deletion by emailing dashhoundapp@gmail.com and
              we will process your request within 30 days.
            </Li>
            <Li>Export your run data at any time from within the App</Li>
          </Section>

          <Section title="14. Data Retention">
            <P>
              We retain your account data for as long as your account is active.
              When you delete your account, all personal data, dog profiles, run
              history, and photos are permanently deleted from our servers
              immediately. We do not retain backups of deleted account data.
            </P>
            <P>
              Anonymized, aggregated data (such as total user counts or breed
              statistics) may be retained for analytics purposes but cannot be
              used to identify you.
            </P>
          </Section>

          <Section title="15. Third-Party Services">
            <P>
              DashHound may link to or display data from third-party services
              (e.g., AKC event pages, map services for directions). These
              third-party sites have their own privacy policies and we are not
              responsible for their content or practices.
            </P>
          </Section>

          <Section title="16. Changes to This Policy">
            <P>
              We may update this Privacy Policy from time to time. We will notify
              you of any material changes by posting the new policy in the App
              and updating the "Last updated" date above. Your continued use of
              the App after changes become effective constitutes your acceptance
              of the updated policy.
            </P>
          </Section>

          <Section title="17. Contact Us">
            <P>
              If you have questions or concerns about this Privacy Policy or our
              data practices, please contact us:
            </P>
            <P style={{ fontWeight: "600", color: NAVY }}>
              dashhoundapp@gmail.com
            </P>
          </Section>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            © 2026 Dash-Hound LLC. All rights reserved.
          </Text>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
  },
  content: {
    flexGrow: 1,
  },
  header: {
    backgroundColor: NAVY,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 20,
    paddingVertical: 14,
    paddingTop: Platform.OS === "ios" ? 50 : 14,
  },
  backButton: {
    width: 60,
  },
  backText: {
    color: "rgba(255,255,255,0.8)",
    fontSize: 15,
    fontWeight: "500",
  },
  headerTitle: {
    color: WHITE,
    fontSize: 16,
    fontWeight: "700",
  },
  hero: {
    backgroundColor: NAVY,
    paddingHorizontal: 24,
    paddingBottom: 40,
    paddingTop: 16,
    alignItems: "center",
  },
  heroTitle: {
    color: WHITE,
    fontSize: 32,
    fontWeight: "900",
    letterSpacing: -0.5,
  },
  heroDate: {
    color: "rgba(255,255,255,0.5)",
    fontSize: 13,
    marginTop: 8,
  },
  body: {
    maxWidth: 720,
    alignSelf: "center",
    width: "100%",
    paddingHorizontal: 24,
    paddingVertical: 40,
    gap: 4,
  },
  section: {
    marginTop: 32,
    gap: 8,
  },
  sectionTitle: {
    color: NAVY,
    fontSize: 18,
    fontWeight: "800",
    marginBottom: 8,
    letterSpacing: -0.2,
  },
  p: {
    color: TEXT,
    fontSize: 15,
    lineHeight: 26,
  },
  li: {
    flexDirection: "row",
    gap: 10,
    paddingLeft: 8,
  },
  liBullet: {
    color: ORANGE,
    fontSize: 16,
    lineHeight: 26,
    flexShrink: 0,
  },
  liText: {
    flex: 1,
    color: TEXT,
    fontSize: 15,
    lineHeight: 26,
  },
  footer: {
    backgroundColor: NAVY_DARK,
    paddingVertical: 32,
    alignItems: "center",
  },
  footerText: {
    color: "rgba(255,255,255,0.35)",
    fontSize: 13,
  },
});
